import React from 'react';
import { FormField, FormSelect } from '../forms';

export type PatchingServiceType = 'WINDOWS' | 'LINUX';
export type PatchingBillingCycle = 'MONTHLY' | 'QUARTERLY';

export interface PatchingServicesFormState {
  /** Empty string = none (optional). At most one of WINDOWS or LINUX. */
  patchingType: PatchingServiceType | '';
  billingCycle: PatchingBillingCycle | '';
  billingAmount: number | null;
}

export const defaultPatchingServicesFormState = (): PatchingServicesFormState => ({
  patchingType: '',
  billingCycle: '',
  billingAmount: null
});

const TYPE_OPTIONS: Array<{ value: PatchingServiceType; label: string }> = [
  { value: 'WINDOWS', label: 'Windows Patching' },
  { value: 'LINUX', label: 'Linux Patching' }
];

const TYPE_LABELS: Record<PatchingServiceType, string> = {
  WINDOWS: 'Windows Patching',
  LINUX: 'Linux Patching'
};

export function patchingServicesFromApi(
  services: Array<{
    patchingType: PatchingServiceType;
    billingCycle: PatchingBillingCycle;
    billingAmount: number;
  }> | undefined | null
): PatchingServicesFormState {
  const list = services ?? [];
  if (list.length === 0) {
    return defaultPatchingServicesFormState();
  }
  const row = list[0];
  return {
    patchingType: row.patchingType,
    billingCycle: row.billingCycle,
    billingAmount: Number(row.billingAmount)
  };
}

export function patchingServicesToApiPayload(
  state: PatchingServicesFormState
): Array<{
  patchingType: PatchingServiceType;
  billingCycle: PatchingBillingCycle;
  billingAmount: number;
}> {
  if (!state.patchingType) {
    return [];
  }

  return [
    {
      patchingType: state.patchingType,
      billingCycle: state.billingCycle as PatchingBillingCycle,
      billingAmount: Number(state.billingAmount ?? 0)
    }
  ];
}

export function validatePatchingServicesForm(state: PatchingServicesFormState): string | null {
  if (!state.patchingType) {
    return null;
  }
  const label = TYPE_LABELS[state.patchingType];
  if (!state.billingCycle) {
    return `Billing cycle is required for ${label}`;
  }
  if (
    state.billingAmount === null ||
    state.billingAmount === undefined ||
    Number.isNaN(state.billingAmount)
  ) {
    return `Billing amount is required for ${label}`;
  }
  if (state.billingAmount < 0) {
    return `Billing amount must be non-negative for ${label}`;
  }
  return null;
}

interface PatchingServicesSectionProps {
  value: PatchingServicesFormState;
  onChange: (next: PatchingServicesFormState) => void;
  currency?: string;
}

export const PatchingServicesSection: React.FC<PatchingServicesSectionProps> = ({
  value,
  onChange,
  currency = 'USD'
}) => {
  const selectType = (patchingType: PatchingServiceType | '') => {
    if (!patchingType) {
      onChange(defaultPatchingServicesFormState());
      return;
    }
    onChange({
      patchingType,
      billingCycle: value.patchingType === patchingType ? value.billingCycle : '',
      billingAmount: value.patchingType === patchingType ? value.billingAmount : null
    });
  };

  return (
    <div className="sm:col-span-2 space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">Patching Services</h4>
        <p className="mt-1 text-sm text-gray-500">
          Optional. Select either Windows Patching or Linux Patching (not both). Billing cycle and
          amount are required when a type is selected.
        </p>
      </div>

      <fieldset className="rounded-lg border border-gray-200 p-4 space-y-3">
        <legend className="sr-only">Patching service type</legend>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="patchingServiceType"
            checked={value.patchingType === ''}
            onChange={() => selectType('')}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          />
          <span className="text-sm text-gray-700">None</span>
        </label>
        {TYPE_OPTIONS.map(({ value: typeValue, label }) => (
          <label key={typeValue} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="patchingServiceType"
              checked={value.patchingType === typeValue}
              onChange={() => selectType(typeValue)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="text-sm font-medium text-gray-900">{label}</span>
          </label>
        ))}
      </fieldset>

      {value.patchingType && (
        <div className="rounded-lg border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-900 mb-4">
            {TYPE_LABELS[value.patchingType]}
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormSelect
              label="Billing Cycle"
              name="patchingBillingCycle"
              value={value.billingCycle}
              onChange={(e) =>
                onChange({
                  ...value,
                  billingCycle: e.target.value as PatchingBillingCycle
                })
              }
              required
              placeholder="Select billing cycle"
              options={[
                { value: 'MONTHLY', label: 'Monthly' },
                { value: 'QUARTERLY', label: 'Quarterly' }
              ]}
            />
            <FormField
              label={`Billing Amount (${currency})`}
              name="patchingBillingAmount"
              type="number"
              min="0"
              step="0.01"
              value={value.billingAmount ?? ''}
              onChange={(e) =>
                onChange({
                  ...value,
                  billingAmount: e.target.value === '' ? null : parseFloat(e.target.value)
                })
              }
              required
              placeholder="Enter amount"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PatchingServicesSection;
